<script setup>
import { watch, computed, ref, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Editor from 'dashboard/components-next/Editor/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ContactNoteItem from 'next/Contacts/ContactsSidebar/components/ContactNoteItem.vue';
import SzCallNoteItem from 'next/Contacts/ContactsSidebar/components/SzCallNoteItem.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const uiFlags = useMapGetter('contactNotes/getUIFlags');
const notesByContact = useMapGetter('contactNotes/getAllNotesByContactId');
const contactGetter = useMapGetter('contacts/getContact');
const isFetchingNotes = computed(() => uiFlags.value.isFetching);
const isCreatingNote = computed(() => uiFlags.value.isCreating);
const contactId = computed(() => props.contactId);
const noteContent = ref('');
const shouldShowCreateModal = ref(false);
const notes = computed(() => {
  if (!contactId.value) {
    return [];
  }
  return notesByContact.value(contactId.value) || [];
});

// SZ: contact phone + calls cruzadas com transcriptions.
const contact = computed(() => contactGetter.value(contactId.value));
const contactPhone = computed(() => contact.value?.phone_number || '');
const calls = ref([]);

async function fetchSzCalls() {
  const phone = contactPhone.value;
  const token = currentUser.value?.access_token;
  if (!phone || !token) {
    calls.value = [];
    return;
  }
  try {
    const url = `/twilio-voice-v2/api/calls/by-phone?phone=${encodeURIComponent(phone)}` +
                `&cw_token=${encodeURIComponent(token)}&limit=100`;
    const res = await fetch(url);
    if (!res.ok) {
      calls.value = [];
      return;
    }
    const data = await res.json();
    calls.value = Array.isArray(data.calls) ? data.calls : [];
  } catch (e) {
    calls.value = [];
  }
}

onMounted(() => { fetchSzCalls(); nextTick(updateScrollState); });
watch(contactId, () => { fetchSzCalls(); });
watch(contactPhone, () => { fetchSzCalls(); });

// SZ: fade gradient no fim da lista (mesmo padrão do ContactPanelHistory).
// Some quando o usuário scrolla até o fim. Mostra também quando não há
// scroll (sinaliza visualmente que ali termina a lista).
const listScrollEl = ref(null);
const isAtBottom = ref(false);
const showFade = computed(() => notes.value.length > 2);

function updateScrollState() {
  const el = listScrollEl.value;
  if (!el) return;
  const hasScroll = el.scrollHeight > el.clientHeight + 2;
  if (!hasScroll) {
    isAtBottom.value = false;
    return;
  }
  isAtBottom.value = el.scrollHeight - el.scrollTop - el.clientHeight <= 2;
}

watch(() => notes.value.length, () => { nextTick(updateScrollState); });

const CALL_NOTE_PREFIX = '**Ligação de Voz**';
const isCallNote = note => typeof note?.content === 'string' &&
                           note.content.startsWith(CALL_NOTE_PREFIX);

function matchCallForNote(note) {
  if (!isCallNote(note) || calls.value.length === 0) return null;
  const dateMatch = note.content.match(/(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})/);
  const agentMatch = note.content.match(/\*\*Agente:\*\*\s+([^|\n]+)/);
  if (!dateMatch || !agentMatch) return null;
  const [, dd, mm, yyyy, hh, mi] = dateMatch;
  const noteTime = new Date(`${yyyy}-${mm}-${dd}T${hh}:${mi}:00`);
  const noteAgent = agentMatch[1].trim().toLowerCase();
  let best = null;
  let bestDiff = Infinity;
  for (const c of calls.value) {
    if ((c.agent_name || '').trim().toLowerCase() !== noteAgent) continue;
    const cTime = new Date(String(c.created_at).replace(' ', 'T') + 'Z');
    const diff = Math.abs(cTime - noteTime);
    if (diff < bestDiff && diff < 30 * 60 * 1000) {
      best = c;
      bestDiff = diff;
    }
  }
  return best;
}

const getWrittenBy = ({ user } = {}) => {
  const currentUserId = currentUser.value?.id;
  return user?.id === currentUserId
    ? t('CONTACTS_LAYOUT.SIDEBAR.NOTES.YOU')
    : user?.name || t('CONVERSATION.BOT');
};

const openCreateModal = () => {
  if (!contactId.value) {
    return;
  }

  noteContent.value = '';
  shouldShowCreateModal.value = true;
};

const closeCreateModal = () => {
  shouldShowCreateModal.value = false;
  noteContent.value = '';
};

const onAdd = async () => {
  if (!contactId.value || !noteContent.value || isCreatingNote.value) {
    return;
  }

  await store.dispatch('contactNotes/create', {
    content: noteContent.value,
    contactId: contactId.value,
  });
  noteContent.value = '';
  closeCreateModal();
};

const onDelete = noteId => {
  if (!contactId.value || !noteId) {
    return;
  }

  store.dispatch('contactNotes/delete', {
    noteId,
    contactId: contactId.value,
  });
};

const keyboardEvents = {
  '$mod+Enter': {
    action: onAdd,
    allowOnFocusedInput: true,
  },
};

useKeyboardEvents(keyboardEvents);

watch(
  contactId,
  newContactId => {
    closeCreateModal();
    if (newContactId) {
      store.dispatch('contactNotes/get', { contactId: newContactId });
    }
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <!-- Header: título + botão Adicionar inline -->
    <div class="flex items-center gap-2 px-4 pt-3 pb-1">
      <h4 class="text-sm font-medium text-n-slate-12 m-0">Notas do contato</h4>
      <button
        class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs cursor-pointer bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4 border-0"
        :disabled="!contactId || isFetchingNotes"
        @click="openCreateModal"
      >
        <span class="i-lucide-plus w-3 h-3" />
        Adicionar
      </button>
    </div>

    <div
      v-if="isFetchingNotes"
      class="flex items-center justify-center py-8 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div v-else-if="notes.length" class="sz-notes-list-wrap">
      <div
        ref="listScrollEl"
        class="sz-notes-list"
        @scroll="updateScrollState"
      >
        <template v-for="note in notes" :key="note.id">
          <!-- SZ: nota gerada pelo twilio-voice-v2 → card de chamada -->
          <SzCallNoteItem
            v-if="isCallNote(note)"
            :note="note"
            :call="matchCallForNote(note)"
            :written-by="getWrittenBy(note)"
          />
          <ContactNoteItem
            v-else
            :note="note"
            :written-by="getWrittenBy(note)"
            allow-delete
            collapsible
            @delete="onDelete"
          />
        </template>
      </div>
      <div
        v-if="showFade"
        class="sz-notes-list-fade"
        :class="{ 'sz-notes-list-fade--hidden': isAtBottom }"
        aria-hidden="true"
      ></div>
    </div>
    <p v-else class="px-4 py-4 text-sm text-n-slate-11 m-0">
      Nenhuma nota adicionada.
    </p>

    <woot-modal
      v-model:show="shouldShowCreateModal"
      :on-close="closeCreateModal"
      :close-on-backdrop-click="false"
    >
      <div class="flex w-full flex-col gap-6 px-6 py-6">
        <h3 class="text-lg font-semibold text-n-slate-12">
          Adicionar nota
        </h3>
        <Editor
          v-model="noteContent"
          focus-on-mount
          :placeholder="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.PLACEHOLDER')"
          class="[&>div]:!border-transparent [&>div]:px-4 [&>div]:py-4"
        />
        <div class="flex items-center justify-end gap-3">
          <NextButton
            faded
            slate
            label="Cancelar"
            @click="closeCreateModal"
          />
          <NextButton
            solid
            blue
            :label="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.SAVE')"
            :is-loading="isCreatingNote"
            :disabled="!noteContent || isCreatingNote"
            @click="onAdd"
          />
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<style>
/* SZ: lista de notas com fade gradient ao fim (mesmo padrão do
   ContactPanelHistory). Estilos não-scoped pra pegar nos componentes
   filhos (SzCallNoteItem, ContactNoteItem) em ambos os temas. */
.sz-notes-list-wrap {
  position: relative;
  --sz-notes-bg: #FFFFFF;
}
.dark .sz-notes-list-wrap {
  --sz-notes-bg: #17181B;
}

.sz-notes-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px;
  max-height: 420px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: #D4D4D4 transparent;
}
.dark .sz-notes-list {
  scrollbar-color: #3E3E43 transparent;
}
.sz-notes-list::-webkit-scrollbar { width: 4px; }
.sz-notes-list::-webkit-scrollbar-track { background: transparent; }
.sz-notes-list::-webkit-scrollbar-thumb {
  background: #D4D4D4;
  border-radius: 4px;
}
.dark .sz-notes-list::-webkit-scrollbar-thumb { background: #3E3E43; }

.sz-notes-list-fade {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 40px;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    var(--sz-notes-bg) 60%,
    var(--sz-notes-bg) 100%
  );
  z-index: 4;
  opacity: 1;
  transition: opacity 0.18s ease;
}
.sz-notes-list-fade--hidden { opacity: 0; }
</style>
